// Short notes at the bottom of the screen for results that have no place of
// their own. A result that belongs to a row or a button is shown there instead.
//
// Promise: `toast(text)` adds a note that leaves after a few seconds; the layer
// (mounted once by the shell) reads the store. Notes are announced politely.
// - Notes stack (two failures in a row both stay readable), up to `MAX`; the
//   same text is not stacked again, its time starts over instead.
// - A note comes in by opening its row while sliding in, and leaves by sliding
//   sideways half its width while fading with an accelerating ease, then its row
//   closes. The text stays until the motion is over (`leaving`).
// - Notes come at the bottom right, the newest last, as the old app had them
//   (2026-10-27; in the bottom middle they ran over the post box's foot). A
//   phone puts them in the middle above its Dock (phone.css). Both keep above
//   the post box an app draws over the page (`--host-box-room`, set while it
//   is there): a failed upload's note lay under it, unreadable.
// - The opacity of the glass is never animated on the way in: a faded parent
//   makes the backdrop blur look different while moving and after. The glass
//   sits on its own layer (WebKit stopped drawing the blur once the motion
//   ended and the layer was folded). It fills a box placed over the note:
//   `.glass` is not in a layer and its `position: relative` beats `absolute`,
//   so a glass span placed itself shrank to nothing and the note had no
//   surface (2026-10-27).
import { useEffect, useState, useSyncExternalStore } from "react";

interface Note {
  id: number;
  text: string;
  /** Sliding out; removed when the motion is over. */
  leaving: boolean;
}

/** How many notes show at once; older ones leave first. */
const MAX = 3;
/** The slide-and-fade part of leaving. */
const FADE_MS = 260;
/** The whole leaving (fade, then the row closes). */
const LEAVE_MS = 460;

let notes: readonly Note[] = [];
let next = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();
const subs = new Set<() => void>();
const set = (list: readonly Note[]) => {
  notes = list;
  for (const fn of subs) fn();
};

function expireIn(id: number, ms: number) {
  clearTimeout(timers.get(id));
  timers.set(
    id,
    setTimeout(() => dismiss(id), ms),
  );
}

function dismiss(id: number) {
  const note = notes.find((n) => n.id === id);
  if (!note || note.leaving) return;
  set(notes.map((n) => (n.id === id ? { ...n, leaving: true } : n)));
  timers.set(
    id,
    setTimeout(() => {
      timers.delete(id);
      set(notes.filter((n) => n.id !== id));
    }, LEAVE_MS),
  );
}

export function toast(text: string, ms = 4000): void {
  const same = notes.find((n) => n.text === text && !n.leaving);
  if (same) {
    expireIn(same.id, ms);
    return;
  }
  const note = { id: next++, text, leaving: false };
  set([...notes, note]);
  expireIn(note.id, ms);
  const showing = notes.filter((n) => !n.leaving);
  for (const old of showing.slice(0, Math.max(0, showing.length - MAX)))
    dismiss(old.id);
}

const subscribe = (fn: () => void) => {
  subs.add(fn);
  return () => subs.delete(fn);
};
const read = () => notes;

export function ToastLayer() {
  const list = useSyncExternalStore(subscribe, read, read);
  return (
    <div
      aria-live="polite"
      // `data-toast-layer`: a phone puts it above its Dock (phone.css).
      data-toast-layer=""
      className="pointer-events-none fixed right-3 bottom-[max(0.75rem,calc(var(--host-box-room,0px)_+_12px))] z-[55] flex w-[min(420px,calc(100vw-24px))] flex-col items-end"
    >
      {list.map((n) => (
        <ToastRow key={n.id} note={n} />
      ))}
    </div>
  );
}

function ToastRow({ note }: { note: Note }) {
  const [entered, setEntered] = useState(false);
  const [settled, setSettled] = useState(false);
  const [collapsing, setCollapsing] = useState(false);
  // Not a fetch: drawing once closed, then open, so the way in has a motion.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  // Not a fetch: clipped only while the row opens or closes (else the shadow is cut).
  useEffect(() => {
    if (!entered) return;
    const timer = setTimeout(() => setSettled(true), 200);
    return () => clearTimeout(timer);
  }, [entered]);
  // Not a fetch: the row closes after the slide-and-fade.
  useEffect(() => {
    if (!note.leaving) return;
    const timer = setTimeout(() => setCollapsing(true), FADE_MS);
    return () => clearTimeout(timer);
  }, [note.leaving]);
  const open = entered && !collapsing;
  return (
    // The row's height eases between 0fr and 1fr (no measuring).
    <div
      aria-hidden={note.leaving || undefined}
      className={`grid w-full transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${
        open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      }`}
    >
      <div
        data-toast-row=""
        className={`-mx-6 flex min-h-0 justify-end px-6 pt-2 ${
          settled && !collapsing ? "overflow-visible" : "overflow-hidden"
        }`}
      >
        <div
          className={`relative isolate inline-flex max-w-full items-center rounded-2xl px-3.5 py-2 text-[12.5px] text-fg transition-[translate,opacity] motion-reduce:transition-none ${
            note.leaving
              ? "translate-x-1/2 opacity-0 duration-[260ms] ease-in"
              : entered
                ? "translate-y-0 duration-200 ease-out"
                : "translate-y-2"
          }`}
        >
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 rounded-[inherit] [transform:translateZ(0)]"
          >
            <span className="glass block size-full rounded-[inherit]" />
          </span>
          <span className="min-w-0">{note.text}</span>
        </div>
      </div>
    </div>
  );
}
